import { Link } from '@tanstack/react-router';
import { Building2, MapPin, MoreHorizontal, Search, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { hasPermission } from '@/lib/rbac';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

import type { ActiveFilter } from './filter-bar/FilterBar';
import { FilterChip } from './filter-bar/FilterChip';
import type { PageHeaderAction } from './PageHeader';

interface ScopeCrumb {
  organizationName: string | null;
  locationName: string | null;
}

export interface ListCardSearch {
  value: string;
  placeholder: string;
  onChange: (next: string) => void;
}

export interface ListCardProps {
  /** Page title shown at the top of the card. When provided the screen no
   *  longer needs a separate `<PageHeader>` element above the card. */
  title: string;
  /** One-line subtitle under the title. */
  description?: string;
  /** Page-level CTAs (Invite user, etc.). Same shape as `PageHeaderAction`
   *  so callers can lift their existing array verbatim. */
  actions?: PageHeaderAction[];
  /** Pluralized noun in mono caps, e.g. "users" / "practitioners" / "clients". */
  countNoun: string;
  /** Total count to display in the title row. */
  count: number | null;
  /**
   * Optional global scope to surface as a soft mint crumb. When both names are
   * `null`, the crumb is hidden entirely. When only the org is set, only the
   * org pill renders.
   */
  scope?: ScopeCrumb;
  /**
   * Inline search input rendered on the left of the toolbar row. The input is
   * the dominant control on the row — count + scope + tools collapse to make
   * room for it.
   */
  search?: ListCardSearch;
  /** Right-aligned tools (density toggle, column visibility, etc.). */
  tools?: ReactNode;
  /** Active filter chips. When non-empty, render as a slim band below the
   *  toolbar row — visible breadcrumb of what's filtering the view. */
  activeFilters?: ActiveFilter[];
  onClearAll?: () => void;
  /** Card body. Pass a `<DataTable embedded hideToolbar />` here for list pages. */
  children: ReactNode;
}

/**
 * Single-card shell for People list pages. Owns:
 *
 *  1. **Page header band** — title, description, and primary CTAs, so the
 *     whole page reads as one composed surface rather than a header floating
 *     above a table card.
 *  2. **Toolbar row** — search (left), count + scope (centre), tools (right).
 *  3. **Chip strip** — only rendered when filters are active.
 *  4. **Body slot** — caller passes the embedded DataTable.
 *
 * Hairlines separate each band; the outer card is `bg-surface rounded-xl
 * shadow-xs border` to match Profile/Section cards used elsewhere.
 */
export function ListCard({
  title,
  description,
  actions,
  countNoun,
  count,
  scope,
  search,
  tools,
  activeFilters,
  onClearAll,
  children,
}: ListCardProps) {
  const chips = activeFilters ?? [];
  const hasChips = chips.length > 0;
  const showClearAll = chips.length >= 2 && onClearAll;
  const hasScope = Boolean(scope?.organizationName);

  return (
    <section
      className={cn(
        'bg-surface overflow-hidden rounded-xl border border-(--color-border) shadow-xs',
      )}
    >
      <PageHeaderBand title={title} description={description} actions={actions} />

      <div
        className={cn(
          'flex flex-wrap items-center gap-x-5 gap-y-3 border-y border-(--color-border) px-4 py-3',
          // When chips render below, the chip strip owns the divider into the
          // table — drop the bottom hairline here so they don't double up.
          hasChips && 'border-b-0',
        )}
      >
        {search ? <SearchInput {...search} /> : <div className="min-w-0 flex-1" />}

        <div className="flex shrink-0 items-center gap-2">
          <CountBadge count={count} noun={countNoun} />
          {hasScope ? (
            <ScopePills
              organizationName={scope?.organizationName ?? null}
              locationName={scope?.locationName ?? null}
            />
          ) : null}
        </div>

        {tools ? (
          <div className="flex shrink-0 items-center gap-3 border-l border-(--color-border) pl-4">
            {tools}
          </div>
        ) : null}
      </div>

      {hasChips ? (
        <div className="flex flex-wrap items-center gap-2 border-y border-(--color-border) bg-neutral-50/40 px-4 py-2">
          {chips.map((f) => (
            <FilterChip key={f.key} label={f.label} onClear={f.onClear} />
          ))}
          {showClearAll ? (
            <button
              type="button"
              onClick={onClearAll}
              className={cn(
                'text-meta-sm ml-1 rounded-sm px-1.5 py-0.5 text-neutral-500 transition-colors',
                'hover:bg-neutral-100 hover:text-neutral-900',
                'focus-visible:shadow-(--shadow-focus-ring-solid) focus-visible:outline-none',
              )}
            >
              Clear all
            </button>
          ) : null}
        </div>
      ) : null}

      {children}
    </section>
  );
}

function PageHeaderBand({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: PageHeaderAction[];
}) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;

  const visibleActions = (actions ?? []).filter(
    (a) => !a.requiredPermission || hasPermission(role, a.requiredPermission),
  );
  const primary = visibleActions.find((a) => a.variant === 'primary');
  const secondaries = visibleActions.filter((a) => a.variant !== 'primary');
  const inlineSecondaries = secondaries.slice(0, 2);
  const overflow = secondaries.slice(2);

  return (
    <header className="flex flex-wrap items-start justify-between gap-4 px-6 py-5">
      <div className="min-w-0 flex-1">
        <h1
          className={cn('font-display text-display-sm text-accent-ink truncate font-medium')}
          title={title}
        >
          {title}
        </h1>
        {description ? (
          <p className="text-body-sm mt-1 line-clamp-2 text-neutral-500">{description}</p>
        ) : null}
      </div>

      {visibleActions.length > 0 ? (
        <div className="flex shrink-0 items-center gap-2">
          {inlineSecondaries.map((action) => (
            <ActionButton key={action.label} action={action} />
          ))}
          {overflow.length > 0 ? <OverflowMenu actions={overflow} /> : null}
          {primary ? <ActionButton action={primary} /> : null}
        </div>
      ) : null}
    </header>
  );
}

const ACTION_VARIANT_MAP: Record<
  NonNullable<PageHeaderAction['variant']>,
  'primary' | 'outline' | 'ghost' | 'outline-destructive'
> = {
  primary: 'primary',
  secondary: 'outline',
  ghost: 'ghost',
  destructive: 'outline-destructive',
};

function ActionButton({ action }: { action: PageHeaderAction }) {
  const Icon = action.icon;
  const variant = ACTION_VARIANT_MAP[action.variant ?? 'secondary'];

  if (action.to) {
    return (
      <Button asChild variant={variant} size="md" disabled={action.disabled}>
        <Link to={action.to as never}>
          {Icon ? <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" /> : null}
          {action.label}
        </Link>
      </Button>
    );
  }
  return (
    <Button variant={variant} size="md" onClick={action.onClick} disabled={action.disabled}>
      {Icon ? <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" /> : null}
      {action.label}
    </Button>
  );
}

function OverflowMenu({ actions }: { actions: PageHeaderAction[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="More actions">
          <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4}>
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            onClick={action.onClick}
            className={cn(
              action.variant === 'destructive' &&
                'text-error-700 focus:text-error-700 focus:bg-error-500/10',
            )}
            disabled={action.disabled}
          >
            {action.icon ? (
              <action.icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
            ) : null}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SearchInput({ value, placeholder, onChange }: ListCardSearch) {
  return (
    <label
      className={cn(
        'group relative flex h-9 max-w-md min-w-0 flex-1 items-center gap-2 rounded-md',
        'border border-(--color-border) bg-neutral-50/40 px-2.5',
        'transition-[box-shadow,border-color] duration-150',
        'focus-within:border-primary-300 focus-within:bg-surface focus-within:shadow-(--shadow-focus-ring)',
      )}
    >
      <Search
        className="h-3.5 w-3.5 shrink-0 text-neutral-400 transition-colors group-focus-within:text-neutral-600"
        strokeWidth={1.75}
        aria-hidden="true"
      />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="text-body-sm flex-1 bg-transparent placeholder:text-neutral-400 focus:outline-none"
      />
      {value ? (
        <button
          type="button"
          onClick={() => {
            onChange('');
          }}
          aria-label="Clear search"
          className={cn(
            'rounded-sm p-0.5 text-neutral-400 transition-colors',
            'hover:bg-neutral-100 hover:text-neutral-700',
            'focus-visible:shadow-(--shadow-focus-ring-solid) focus-visible:outline-none',
          )}
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      ) : null}
    </label>
  );
}

function CountBadge({ count, noun }: { count: number | null; noun: string }) {
  return (
    <span className="text-meta-sm tracking-label inline-flex items-baseline gap-1 text-neutral-500 uppercase">
      <span className="text-accent-ink/85 font-semibold">
        {count === null ? '—' : count.toLocaleString()}
      </span>
      <span className="font-medium">{noun}</span>
    </span>
  );
}

function ScopePills({
  organizationName,
  locationName,
}: {
  organizationName: string | null;
  locationName: string | null;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="text-neutral-300" aria-hidden="true">
        ·
      </span>
      {organizationName ? (
        <span
          className={cn(
            'bg-primary-50 text-primary-700 ring-primary-100/70 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1',
          )}
        >
          <Building2 className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
          {organizationName}
        </span>
      ) : null}
      {locationName ? (
        <span
          className={cn(
            'bg-primary-50 text-primary-700 ring-primary-100/70 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1',
          )}
        >
          <MapPin className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
          {locationName}
        </span>
      ) : null}
    </span>
  );
}
